export type LanguageMessageMode = "same" | "perLanguage";

export type LanguageMessage = {
  mode: LanguageMessageMode;
  text: string;
  variants: Record<string, string>;
  order: string[];
};

export type SupportedLanguage = { code: string; name: string };

export type LanguageStatusState = "filled" | "empty" | "unsupported";

export type LanguageStatus = {
  code: string;
  name: string;
  state: LanguageStatusState;
  isDefault: boolean;
};

const OPENER_SOURCE = '<lang\\s*=\\s*["\'“”]?([A-Za-z][A-Za-z0-9-]*)["\'“”]?\\s*>';
const TERMINATOR_SOURCE = "<\\/?lang\\s*>";

const openerRegex = () => new RegExp(OPENER_SOURCE, "gi");
const terminatorRegex = () => new RegExp(TERMINATOR_SOURCE, "gi");

const normalizeCode = (code: string | undefined | null): string => (code ?? "").trim().toLowerCase();

export function isTaggedMessage(text: string): boolean {
  if (typeof text !== "string" || !text.toLowerCase().includes("<lang")) return false;
  return openerRegex().test(text);
}

export function parseLanguageMessage(text: string): LanguageMessage {
  const raw = typeof text === "string" ? text : "";
  const same: LanguageMessage = { mode: "same", text: raw, variants: {}, order: [] };
  if (!isTaggedMessage(raw)) return same;

  const opener = openerRegex();
  const openers: Array<{ code: string; start: number; contentStart: number }> = [];
  let match: RegExpExecArray | null;
  while ((match = opener.exec(raw)) !== null) {
    openers.push({ code: match[1].toLowerCase(), start: match.index, contentStart: match.index + match[0].length });
  }

  const variants: Record<string, string> = {};
  const order: string[] = [];
  for (let i = 0; i < openers.length; i++) {
    const end = i + 1 < openers.length ? openers[i + 1].start : raw.length;
    const window = raw.slice(openers[i].contentStart, end);
    const terminator = terminatorRegex().exec(window);
    const content = (terminator ? window.slice(0, terminator.index) : window).trim();
    if (content.length === 0) continue;
    if (variants[openers[i].code] !== undefined) continue;
    variants[openers[i].code] = content;
    order.push(openers[i].code);
  }

  if (order.length === 0) return same;
  return { mode: "perLanguage", text: "", variants, order };
}

export function serializeLanguageMessage(message: LanguageMessage, defaultLanguage?: string): string {
  if (message.mode === "same") return message.text ?? "";

  const defaultCode = normalizeCode(defaultLanguage);
  const codes: string[] = [];
  if (defaultCode && message.variants[defaultCode] !== undefined) codes.push(defaultCode);
  for (const code of message.order) if (!codes.includes(code)) codes.push(code);
  for (const code of Object.keys(message.variants)) if (!codes.includes(code)) codes.push(code);

  return codes
    .map((code) => ({ code, text: (message.variants[code] ?? "").trim() }))
    .filter(({ text }) => text.length > 0)
    .map(({ code, text }) => `<lang="${code}">${text}</lang>`)
    .join("\n");
}

export function languageStatuses(
  message: LanguageMessage,
  supported: SupportedLanguage[],
  defaultLanguage?: string
): LanguageStatus[] {
  const defaultCode = normalizeCode(defaultLanguage);
  const filledSame = message.mode === "same" && (message.text ?? "").trim().length > 0;

  const rows: LanguageStatus[] = (supported ?? []).map(({ code, name }) => {
    const normalized = normalizeCode(code);
    const filled = message.mode === "same" ? filledSame : (message.variants[normalized] ?? "").trim().length > 0;
    return { code: normalized, name, state: filled ? "filled" : "empty", isDefault: normalized === defaultCode };
  });

  if (message.mode === "perLanguage") {
    for (const code of message.order) {
      if (!rows.some((row) => row.code === code)) {
        rows.push({ code, name: code, state: "unsupported", isDefault: code === defaultCode });
      }
    }
  }
  return rows;
}

export function toPerLanguage(message: LanguageMessage, defaultLanguage?: string): LanguageMessage {
  if (message.mode === "perLanguage") return message;
  const code = normalizeCode(defaultLanguage) || "en";
  const text = (message.text ?? "").trim();
  return text.length > 0
    ? { mode: "perLanguage", text: "", variants: { [code]: text }, order: [code] }
    : { mode: "perLanguage", text: "", variants: {}, order: [] };
}

export function toSameForAll(message: LanguageMessage, defaultLanguage?: string): LanguageMessage {
  if (message.mode === "same") return message;
  const defaultCode = normalizeCode(defaultLanguage);
  const text = message.variants[defaultCode] ?? message.variants[message.order[0]] ?? "";
  return { mode: "same", text, variants: {}, order: [] };
}
